import React, { useCallback, useEffect, useState } from "react";
import TextField from "../../../Components/UI/Inputs/TextField";
import DropDown from "../../../Components/UI/Inputs/DropDown";
import Button from "../../../Components/UI/Buttons/Button";
import { Checkbox, FormControlLabel } from "@mui/material";
import DateTime from "../../../Components/UI/Inputs/DateTime";
import useApi from "../../../Hooks/useApi";
import showToast from "../../../Helpers/toast";
import { useNavigate } from "react-router-dom";
import MultiLocationSelector from "../../../Components/UI/Inputs/LocationSelector/LocationSelectorContainer";
import { handleApiError } from "../../../Helpers/ErrorHandler";
import Common from "../../../Components/Layouts/Common";

export const RECURRENT_OPTIONS = [
  { name: "Day", value: "DAY" },
  { name: "Week", value: "WEEK" },
  { name: "Month", value: "MONTH" },
  { name: "Year", value: "YEAR" },
];

const CreateKII = () => {
  const { request } = useApi();
  const [locations, setLocations] = useState([]);
  const [questionnaireList, setQuestionnaireList] = useState(null);
  const [allLocation, setAllLocation] = useState([]);

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    questionnaireId: "",
    locations: [],
  });
  const [recurrentData, setRecurrentData] = useState({
    recurrent: false,
    interval: 0,
    recurrentType: "MONTH",
  });

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      questionnaireId: formData.questionnaireId,
      locations: locations?.map((location) => location?.id),
    };

    if (!data.questionnaireId) {
      showToast({ type: "error", message: "Questionnaire not selected" });
      return;
    }

    if (!(data.locations.length > 0)) {
      showToast({ type: "error", message: "Select at least one location." });
      return;
    }

    if (recurrentData.recurrent) {
      if (!recurrentData.interval) {
        showToast({ type: "error", message: "Specify recurrence interval." });
        return;
      }
      if (!recurrentData.recurrentType) {
        showToast({ type: "error", message: "Select type of recurrence." });
        return;
      }

      data.recurrent = recurrentData.recurrent;
      data.recurrentType = recurrentData.recurrentType;
      data.interval = recurrentData.interval;
    }

    try {
      const response = await request(
        "POST",
        "/v1/kii",
        {
          data,
        },
        "kii_service"
      );

      showToast({ message: response.message });
      setFormData({
        name: "",
        startDate: "",
        endDate: "",
        questionnaireId: "",
        locations: null,
        recurrent: false,
        interval: 0,
        recurrenceType: "MONTH",
      });

      setTimeout(() => {
        navigate("/manage-kii");
      }, 1500);
    } catch (error) {
      // showToast({ type: "error", message: error?.response?.data?.message || "Error Happened" })
      handleApiError(error);
    }
  };

  const isEndDateValid = () => {
    if (!formData.startDate || !formData.endDate) return true;
    return new Date(formData.endDate) >= new Date(formData.startDate);
  };

  const fetchQuestionnaireList = useCallback(async () => {
    try {
      const response = await request(
        "GET",
        "/v1/kii/questionnaires/list?status=COMPLETED",
        {},
        "kii_service"
      );

      if (response.content && response.content.length > 0) {
        setQuestionnaireList(
          response.content.map((questionnaireItem) => ({
            name: questionnaireItem.name,
            value: questionnaireItem.id,
          }))
        );
      }
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  }, [request]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await request(
        "GET",
        "/v1/kii/locations",
        {},
        "kii_service"
      );
      if (response?.length > 0) {
        setAllLocation(response);
      }
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  }, [request]);

  useEffect(() => {
    fetchLocations();
    fetchQuestionnaireList();
  }, [fetchLocations, fetchQuestionnaireList]);


  return (
    <div className=" bg-white relative h-full">
      <Common headline={"Create New KII"}>
        <form onSubmit={handleSubmit} className="text-start space-y-4 px-2 h-full">
          <TextField
            placeholder="Enter KII Title"
            name="name"
            label={"KII Title"}
            value={formData.name}
            onValueChange={handleInputChange}
            required
          />
          <div className="grid grid-cols-2 gap-2 items-center ">
            <DropDown
              name="questionnaireId"
              value={formData.questionnaireId}
              label="Select Questionnaire"
              onValueChange={(value) =>
                handleInputChange("questionnaireId", value)
              }
              placeholder={"Select Questionnaire"}
              options={questionnaireList || []}
            />

            <MultiLocationSelector
              locationList={allLocation}
              locations={locations}
              setLocations={setLocations}
            />

            <DateTime
              label={"Start Date"}
              type="date"
              placeholder="Start Date"
              name="startDate"
              value={formData.startDate}
              onValueChange={handleInputChange}
              required
            />

            <DateTime
              label={"End Date"}
              type="date"
              minDate={formData.startDate}
              placeholder="End Date"
              name="endDate"
              value={formData.endDate}
              onValueChange={handleInputChange}
              required
              min={formData.startDate}
            />
            <div className="col-span-full grid grid-cols-8 gap-6  w-full">
              <div className="col-span-2 my-auto">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={recurrentData?.recurrent}
                      onChange={(e) => {
                        setRecurrentData({
                          ...recurrentData,
                          recurrent: e.target.checked,
                        });
                      }}
                    />
                  }
                  label="Recurrent"
                />
              </div>
              {recurrentData.recurrent ? (
                <>
                  <div className="col-span-3">
                    <TextField
                      name="interval"
                      type="number"
                      min={0}
                      label="Recurrence Interval"
                      placeholder={"Recurrence Interval"}
                      onValueChange={(name, value) => {
                        setRecurrentData({
                          ...recurrentData,
                          [name]: parseInt(value || 0),
                        });
                      }}
                    />
                  </div>
                  <div className="col-span-3">
                    <DropDown
                      name="recurrenceType"
                      value={recurrentData.recurrentType}
                      label="Recurrence Period Unit"
                      placeholder={"Recurrence Type"}
                      onValueChange={(value) => {
                        setRecurrentData({
                          ...recurrentData,
                          recurrentType: value,
                        });
                      }}
                      options={RECURRENT_OPTIONS}
                    />
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
          </div>

          {!isEndDateValid() && (
            <p className="text-red-500 text-sm">
              End date cannot be before start date.
            </p>
          )}

          {/* <Button
            type="small_bordered"
            label="Submit"
            backgroundColor="#2F4B7C"
            textColor="white"
            style={{
              height: "40px",
              width: "100%",
            }}
            disabled={!isEndDateValid()}
          /> */}

     <div className="flex justify-center ">
           <Button
            type="full"
            label="Submit"
          
            style={{
        
              width: "200px",
            }}
            disabled={!isEndDateValid()}
          />
   </div>
        </form>
      </Common>
    </div>
  );
};

export default CreateKII;
